import { customEvent } from "src/utils/gtag.js";

/**
 * The section's identity as an event_label ("nba-playoffs (algorithmic)") —
 * GA4 only surfaces registered dimensions, so the slug/source variant rides in
 * the standard label instead of custom params.
 *
 * @param {object|undefined} section
 * @returns {string}
 */
function sectionLabel(section) {
  const slug = section?.slug ?? "";
  return section?.source ? `${slug} (${section.source})` : slug;
}

/**
 * Builds the standard dimensions a section event carries. Section identity,
 * user, and calendar fields are named GA4 dimensions.
 *
 * @param {{ user?: object, section?: object, calendar?: object }} params
 * @returns {object}
 */
function sectionEventData({ user, section, calendar }) {
  return {
    ...(section
      ? {
          section_slug: section.slug,
          section_type: section.type ?? section.source,
        }
      : {}),
    ...(user ? { user_id: user._id, user_email: user.email } : {}),
    ...(calendar
      ? {
          calendar_id: calendar.calendarId,
          calendar_handle: calendar.handle,
          calendar_name: calendar.name,
        }
      : {}),
  };
}

/**
 * Fires once per rail when it first scrolls into view.
 *
 * @param {{ user?: object, section: object }} params
 */
export function trackRailViewed({ user, section }) {
  customEvent({
    name: "section_rail_viewed",
    category: "Discovery",
    action: "view",
    label: sectionLabel(section),
    data: sectionEventData({ user, section }),
  });
}

/**
 * Fires when a calendar tile inside a rail is opened.
 *
 * @param {{ user?: object, section: object, calendar: object }} params
 */
export function trackTileClicked({ user, section, calendar }) {
  customEvent({
    name: "section_tile_clicked",
    category: "Discovery",
    label: sectionLabel(section),
    data: sectionEventData({ user, section, calendar }),
  });
}

/**
 * Fires when a rail's "See all" link is followed.
 *
 * @param {{ user?: object, section: object }} params
 */
export function trackSeeAllClicked({ user, section }) {
  customEvent({
    name: "section_see_all_clicked",
    category: "Discovery",
    label: sectionLabel(section),
    data: sectionEventData({ user, section }),
  });
}

/**
 * Fires when a navigation item routes (sidebar, browse sheet, bottom tabs).
 *
 * @param {{ user?: object, surface: string, item: string }} params
 */
export function trackNavItemClicked({ user, surface, item }) {
  customEvent({
    name: "nav_item_clicked",
    category: "Discovery",
    label: `${item} (${surface})`,
    data: sectionEventData({ user }),
  });
}
